import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHospitalOnboarding } from '../../../contexts/HospitalOnboardingContext';
import {
  Users,
  Plus,
  Trash2,
  Mic,
  ArrowRight,
  Bookmark,
  ChevronDown,
  Check
} from 'lucide-react';

const CLIENT_TYPES = ['Hospital', 'Corporate', 'Insurance', 'Direct Patient'];
const PAYMENT_MODES = ['Cash', 'Credit / Invoice (30 Days)', 'UPI / Online', 'Insurance TPA'];
const PICKUP_TYPES = ['Emergency', 'Scheduled Transfer', 'Discharge', 'Accident Zone'];

const LOST_RIDE_REASONS = [
  'No ambulance available',
  'Vehicle too far',
  'Rate too high',
  'ALS unavailable',
  'Driver unavailable',
  'Long distance',
  'Payment / credit issue',
  'Customer cancellation',
  'Competitor selected'
];

export const Step3RidesClients = () => {
  const navigate = useNavigate();
  const { formData, updateFields, saveAndContinue, goBack, saveAndExit, isSaving } = useHospitalOnboarding();

  // Monthly rides state
  const [monthlyRides, setMonthlyRides] = useState(() => {
    const raw = formData.monthly_rides || {};
    if (raw.completed_rides === '180' || raw.average_fare === '2500') {
      return {
        completed_rides: '',
        enquiries: '',
        lost_rides: '',
        average_fare: '',
        average_trip_km: '',
        data_confidence: ''
      };
    }
    return {
      completed_rides: raw.completed_rides || '',
      enquiries: raw.enquiries || '',
      lost_rides: raw.lost_rides || '',
      average_fare: raw.average_fare || '',
      average_trip_km: raw.average_trip_km || '',
      data_confidence: raw.data_confidence || ''
    };
  });

  // Top clients table
  const [topClients, setTopClients] = useState(() => {
    if (formData.top_clients && formData.top_clients.length > 0) {
      return formData.top_clients;
    }
    return [
      {
        id: 'tc-1',
        client: '',
        type: '',
        area: '',
        rides_month: '',
        average_fare: '',
        payment: ''
      }
    ];
  });

  // Top pickup areas table
  const [pickupAreas, setPickupAreas] = useState(() => {
    if (formData.top_pickup_areas && formData.top_pickup_areas.length > 0) {
      return formData.top_pickup_areas;
    }
    return [
      {
        id: 'pa-1',
        area_pin: '',
        peak_time: '',
        type: ''
      }
    ];
  });

  // Long distance corridors table
  const [corridors, setCorridors] = useState(() => {
    if (formData.long_distance_corridors && formData.long_distance_corridors.length > 0) {
      return formData.long_distance_corridors;
    }
    return [
      {
        id: 'ld-1',
        pickup: '',
        destination: '',
        trips_month: '',
        km: '',
        typical_rate: ''
      }
    ];
  });

  // Lost reasons array
  const [lostReasons, setLostReasons] = useState(() => {
    if (Array.isArray(formData.lost_ride_reasons)) {
      return formData.lost_ride_reasons;
    }
    return [];
  });

  const handleMonthlyChange = (field, val) => {
    const updated = { ...monthlyRides, [field]: val };
    setMonthlyRides(updated);
    updateFields({ monthly_rides: updated });
  };

  const handleAddClientRow = () => {
    const newRow = {
      id: typeof window !== 'undefined' && window.crypto?.randomUUID ? `tc-${window.crypto.randomUUID()}` : `tc-${Math.random().toString(36).slice(2, 9)}`,
      client: '',
      type: 'Hospital',
      area: '',
      rides_month: '',
      average_fare: '',
      payment: 'Credit / Invoice (30 Days)'
    };
    const updated = [...topClients, newRow];
    setTopClients(updated);
    updateFields({ top_clients: updated });
  };

  const handleRemoveClientRow = (id) => {
    if (topClients.length <= 1) return;
    const updated = topClients.filter((r) => r.id !== id);
    setTopClients(updated);
    updateFields({ top_clients: updated });
  };

  const handleClientChange = (id, field, value) => {
    const updated = topClients.map((r) => (r.id === id ? { ...r, [field]: value } : r));
    setTopClients(updated);
    updateFields({ top_clients: updated });
  };

  const handleAddPickupRow = () => {
    const newRow = {
      id: typeof window !== 'undefined' && window.crypto?.randomUUID ? `pa-${window.crypto.randomUUID()}` : `pa-${Math.random().toString(36).slice(2, 9)}`,
      area_pin: '',
      peak_time: '',
      type: 'Emergency'
    };
    const updated = [...pickupAreas, newRow];
    setPickupAreas(updated);
    updateFields({ top_pickup_areas: updated });
  };

  const handleRemovePickupRow = (id) => {
    if (pickupAreas.length <= 1) return;
    const updated = pickupAreas.filter((r) => r.id !== id);
    setPickupAreas(updated);
    updateFields({ top_pickup_areas: updated });
  };

  const handlePickupChange = (id, field, value) => {
    const updated = pickupAreas.map((r) => (r.id === id ? { ...r, [field]: value } : r));
    setPickupAreas(updated);
    updateFields({ top_pickup_areas: updated });
  };

  const handleAddCorridorRow = () => {
    const newRow = {
      id: typeof window !== 'undefined' && window.crypto?.randomUUID ? `ld-${window.crypto.randomUUID()}` : `ld-${Math.random().toString(36).slice(2, 9)}`,
      pickup: '',
      destination: '',
      trips_month: '',
      km: '',
      typical_rate: ''
    };
    const updated = [...corridors, newRow];
    setCorridors(updated);
    updateFields({ long_distance_corridors: updated });
  };

  const handleRemoveCorridorRow = (id) => {
    if (corridors.length <= 1) return;
    const updated = corridors.filter((r) => r.id !== id);
    setCorridors(updated);
    updateFields({ long_distance_corridors: updated });
  };

  const handleCorridorChange = (id, field, value) => {
    const updated = corridors.map((r) => (r.id === id ? { ...r, [field]: value } : r));
    setCorridors(updated);
    updateFields({ long_distance_corridors: updated });
  };

  const toggleLostReason = (reason) => {
    const updated = lostReasons.includes(reason)
      ? lostReasons.filter((r) => r !== reason)
      : [...lostReasons, reason];
    setLostReasons(updated);
    updateFields({ lost_ride_reasons: updated });
  };

  const handleContinue = (e) => {
    e.preventDefault();
    updateFields({
      monthly_rides: monthlyRides,
      top_clients: topClients,
      top_pickup_areas: pickupAreas,
      long_distance_corridors: corridors,
      lost_ride_reasons: lostReasons
    });
    saveAndContinue();
  };

  return (
    <div className="w-full">
      {/* Header Row: Step info and Switch to Voice (Image 2) */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <span className="text-xs font-semibold uppercase tracking-wider text-blue-600 block mb-1">
            STEP 3 OF 6
          </span>
          <h2 className="text-2xl sm:text-3xl font-semibold text-slate-800 tracking-tight leading-tight">
            Rides & clients
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Share your existing ride patterns and regular clients.
          </p>
          {/* Smart Ambulance Blue accent line (NOT red!) */}
          <div className="w-14 sm:w-16 h-0.5 bg-blue-600 mt-2 rounded-full" />
        </div>

        <button
          type="button"
          onClick={() => navigate('/hospital/onboarding/voice?type=fleet&step=3')}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-medium shadow-2xs transition-colors self-start cursor-pointer"
        >
          <Mic className="w-3.5 h-3.5 text-slate-600" />
          <span>Switch to voice</span>
        </button>
      </div>

      <form onSubmit={handleContinue} className="space-y-6">
        {/* Card 1: Monthly ride details (Image 2) */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7 shadow-xs">
          <div className="pb-4 mb-5 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-800">Monthly ride details</h3>
            <p className="text-xs text-slate-400">Estimated historical ride volume and performance metrics</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Approx. Completed Rides / Month
              </label>
              <input
                type="number"
                placeholder="Enter approx. completed rides / month"
                value={monthlyRides.completed_rides}
                onChange={(e) => handleMonthlyChange('completed_rides', e.target.value)}
                className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Approx. Enquiries / Month
              </label>
              <input
                type="number"
                placeholder="Enter approx. enquiries / month"
                value={monthlyRides.enquiries}
                onChange={(e) => handleMonthlyChange('enquiries', e.target.value)}
                className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Approx. Lost / Rejected / Month
              </label>
              <input
                type="number"
                placeholder="Enter approx. lost / rejected / month"
                value={monthlyRides.lost_rides}
                onChange={(e) => handleMonthlyChange('lost_rides', e.target.value)}
                className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Average Fare (₹)
              </label>
              <input
                type="number"
                placeholder="Enter average fare"
                value={monthlyRides.average_fare}
                onChange={(e) => handleMonthlyChange('average_fare', e.target.value)}
                className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 font-semibold"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Average Trip KM
              </label>
              <input
                type="number"
                placeholder="Enter average trip km"
                value={monthlyRides.average_trip_km}
                onChange={(e) => handleMonthlyChange('average_trip_km', e.target.value)}
                className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-slate-700 mb-1">
                Data Confidence
              </label>
              <div className="relative">
                <select
                  value={monthlyRides.data_confidence || ''}
                  onChange={(e) => handleMonthlyChange('data_confidence', e.target.value)}
                  className="w-full px-3.5 py-2.5 pr-8 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 appearance-none cursor-pointer"
                >
                  <option value="">Select data confidence</option>
                  <option value="High - Verified Logs">High - Verified Digital Logs</option>
                  <option value="Moderate - Estimated">Moderate - Estimated From Dispatch Records</option>
                  <option value="Low - Rough Approximation">Low - Rough Operational Approximation</option>
                </select>
                <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
              </div>
            </div>
          </div>
        </div>

        {/* Card 2: Top hospitals / clients (Image 2) */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7 shadow-xs">
          <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-100">
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Top hospitals / clients</h3>
              <p className="text-xs text-slate-400">Regular institutional and corporate booking partners</p>
            </div>
            <button
              type="button"
              onClick={handleAddClientRow}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 hover:border-slate-300 bg-white text-xs font-bold text-blue-600 hover:bg-blue-50 transition-colors cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Add row</span>
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-slate-200 text-slate-500 font-semibold">
                  <th className="py-2.5 px-2.5 w-8">#</th>
                  <th className="py-2.5 px-2.5 min-w-[170px]">Client</th>
                  <th className="py-2.5 px-2.5 min-w-[120px]">Type</th>
                  <th className="py-2.5 px-2.5 min-w-[120px]">Area</th>
                  <th className="py-2.5 px-2.5 min-w-[100px]">Rides / Month</th>
                  <th className="py-2.5 px-2.5 min-w-[110px]">Average Fare</th>
                  <th className="py-2.5 px-2.5 min-w-[160px]">Payment</th>
                  <th className="py-2.5 px-2.5 w-8 text-center"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {topClients.map((row, idx) => (
                  <tr key={row.id} className="hover:bg-slate-50/60 transition-colors">
                    <td className="py-2.5 px-2.5 font-semibold text-slate-500">{idx + 1}</td>
                    <td className="py-2.5 px-2.5">
                      <input
                        type="text"
                        placeholder="Enter client name"
                        value={row.client}
                        onChange={(e) => handleClientChange(row.id, 'client', e.target.value)}
                        className="w-full py-1.5 px-2.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600"
                      />
                    </td>
                    <td className="py-2.5 px-2.5">
                      <div className="relative">
                        <select
                          value={row.type}
                          onChange={(e) => handleClientChange(row.id, 'type', e.target.value)}
                          className="w-full py-1.5 pl-2.5 pr-7 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 appearance-none cursor-pointer"
                        >
                          <option value="">Select type</option>
                          {CLIENT_TYPES.map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                        <ChevronDown className="w-3 h-3 text-slate-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                      </div>
                    </td>
                    <td className="py-2.5 px-2.5">
                      <input
                        type="text"
                        placeholder="Enter area"
                        value={row.area}
                        onChange={(e) => handleClientChange(row.id, 'area', e.target.value)}
                        className="w-full py-1.5 px-2.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600"
                      />
                    </td>
                    <td className="py-2.5 px-2.5">
                      <input
                        type="number"
                        placeholder="Rides/month"
                        value={row.rides_month}
                        onChange={(e) => handleClientChange(row.id, 'rides_month', e.target.value)}
                        className="w-full py-1.5 px-2.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600"
                      />
                    </td>
                    <td className="py-2.5 px-2.5">
                      <input
                        type="number"
                        placeholder="Average fare"
                        value={row.average_fare}
                        onChange={(e) => handleClientChange(row.id, 'average_fare', e.target.value)}
                        className="w-full py-1.5 px-2.5 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 font-semibold"
                      />
                    </td>
                    <td className="py-2.5 px-2.5">
                      <div className="relative">
                        <select
                          value={row.payment}
                          onChange={(e) => handleClientChange(row.id, 'payment', e.target.value)}
                          className="w-full py-1.5 pl-2.5 pr-7 bg-white border border-slate-200 rounded-lg text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 appearance-none cursor-pointer"
                        >
                          <option value="">Select payment mode</option>
                          {PAYMENT_MODES.map((p) => (
                            <option key={p} value={p}>{p}</option>
                          ))}
                        </select>
                        <ChevronDown className="w-3 h-3 text-slate-400 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                      </div>
                    </td>
                    <td className="py-2.5 px-2.5 text-center">
                      {topClients.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveClientRow(row.id)}
                          className="text-slate-400 hover:text-red-500 transition-colors cursor-pointer"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Card 3: Top pickup areas - Hidden per onboarding requirement
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7 shadow-xs">
          <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-100">
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Top pickup areas</h3>
              <p className="text-xs text-slate-400">High-demand geographical localities and peak dispatch windows</p>
            </div>
            <button
              type="button"
              onClick={handleAddPickupRow}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 hover:border-slate-300 bg-white text-xs font-bold text-blue-600 hover:bg-blue-50 transition-colors cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Add row</span>
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-slate-200 text-slate-500 font-semibold">
                  <th className="py-2.5 px-3 w-10">#</th>
                  <th className="py-2.5 px-3 min-w-[200px]">Area / PIN</th>
                  <th className="py-2.5 px-3 min-w-[180px]">Peak Time</th>
                  <th className="py-2.5 px-3 min-w-[160px]">Type</th>
                  <th className="py-2.5 px-3 w-10 text-center"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pickupAreas.map((row, idx) => (
                  <tr key={row.id} className="hover:bg-slate-50/60 transition-colors">
                    <td className="py-3 px-3 font-semibold text-slate-500">{idx + 1}</td>
                    <td className="py-3 px-3">
                      <input
                        type="text"
                        placeholder="Enter area / PIN"
                        value={row.area_pin}
                        onChange={(e) => handlePickupChange(row.id, 'area_pin', e.target.value)}
                        className="w-full py-2 px-3 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600"
                      />
                    </td>
                    <td className="py-3 px-3">
                      <input
                        type="text"
                        placeholder="Enter peak hours"
                        value={row.peak_time}
                        onChange={(e) => handlePickupChange(row.id, 'peak_time', e.target.value)}
                        className="w-full py-2 px-3 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600"
                      />
                    </td>
                    <td className="py-3 px-3">
                      <div className="relative">
                        <select
                          value={row.type}
                          onChange={(e) => handlePickupChange(row.id, 'type', e.target.value)}
                          className="w-full py-2 pl-3 pr-8 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 appearance-none cursor-pointer"
                        >
                          <option value="">Select pickup type</option>
                          {PICKUP_TYPES.map((t) => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                        <ChevronDown className="w-3.5 h-3.5 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
                      </div>
                    </td>
                    <td className="py-3 px-3 text-center">
                      {pickupAreas.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemovePickupRow(row.id)}
                          className="text-slate-400 hover:text-red-500 transition-colors cursor-pointer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        */}

        {/* Card 4: Long-distance corridors (Image 2) */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7 shadow-xs">
          <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-100">
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Long-distance corridors</h3>
              <p className="text-xs text-slate-400">Regular intercity routes and transfer corridors</p>
            </div>
            <button
              type="button"
              onClick={handleAddCorridorRow}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl border border-slate-200 hover:border-slate-300 bg-white text-xs font-bold text-blue-600 hover:bg-blue-50 transition-colors cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Add row</span>
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-slate-200 text-slate-500 font-semibold">
                  <th className="py-2.5 px-3 w-10">#</th>
                  <th className="py-2.5 px-3 min-w-[140px]">Pickup</th>
                  <th className="py-2.5 px-3 min-w-[140px]">Destination</th>
                  <th className="py-2.5 px-3 min-w-[100px]">Trips / Month</th>
                  <th className="py-2.5 px-3 min-w-[80px]">KM</th>
                  <th className="py-2.5 px-3 min-w-[110px]">Typical Rate</th>
                  <th className="py-2.5 px-3 w-10 text-center"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {corridors.map((row, idx) => (
                  <tr key={row.id} className="hover:bg-slate-50/60 transition-colors">
                    <td className="py-3 px-3 font-semibold text-slate-500">{idx + 1}</td>
                    <td className="py-3 px-3">
                      <input
                        type="text"
                        placeholder="Enter pickup"
                        value={row.pickup}
                        onChange={(e) => handleCorridorChange(row.id, 'pickup', e.target.value)}
                        className="w-full py-2 px-3 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600"
                      />
                    </td>
                    <td className="py-3 px-3">
                      <input
                        type="text"
                        placeholder="Enter destination"
                        value={row.destination}
                        onChange={(e) => handleCorridorChange(row.id, 'destination', e.target.value)}
                        className="w-full py-2 px-3 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600"
                      />
                    </td>
                    <td className="py-3 px-3">
                      <input
                        type="number"
                        placeholder="Trips/month"
                        value={row.trips_month}
                        onChange={(e) => handleCorridorChange(row.id, 'trips_month', e.target.value)}
                        className="w-full py-2 px-3 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600"
                      />
                    </td>
                    <td className="py-3 px-3">
                      <input
                        type="number"
                        placeholder="Distance (km)"
                        value={row.km}
                        onChange={(e) => handleCorridorChange(row.id, 'km', e.target.value)}
                        className="w-full py-2 px-3 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600"
                      />
                    </td>
                    <td className="py-3 px-3">
                      <input
                        type="number"
                        placeholder="Typical rate (₹)"
                        value={row.typical_rate}
                        onChange={(e) => handleCorridorChange(row.id, 'typical_rate', e.target.value)}
                        className="w-full py-2 px-3 bg-white border border-slate-200 rounded-xl text-xs text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-600 font-semibold"
                      />
                    </td>
                    <td className="py-3 px-3 text-center">
                      {corridors.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveCorridorRow(row.id)}
                          className="text-slate-400 hover:text-red-500 transition-colors cursor-pointer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Card 5: Reasons for lost rides (Image 2) */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7 shadow-xs">
          <div className="pb-3 mb-4 border-b border-slate-100">
            <h3 className="text-sm font-semibold text-slate-800">Reasons for lost rides</h3>
            <p className="text-xs text-slate-400">What factors prevent your fleet from completing enquiries?</p>
          </div>

          <div className="flex flex-wrap gap-2.5">
            {LOST_RIDE_REASONS.map((reason) => {
              const isChecked = lostReasons.includes(reason);
              return (
                <button
                  key={reason}
                  type="button"
                  onClick={() => toggleLostReason(reason)}
                  className={`px-3 py-2 rounded-xl border text-xs font-medium transition-all flex items-center gap-2 ${
                    isChecked
                      ? 'border-blue-600 bg-blue-50 text-blue-700 shadow-2xs font-semibold'
                      : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                  }`}
                >
                  <div
                    className={`w-4 h-4 rounded-md flex items-center justify-center border transition-colors ${
                      isChecked
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : 'border-slate-300 bg-white'
                    }`}
                  >
                    {isChecked && <Check className="w-2.5 h-2.5 stroke-[3]" />}
                  </div>
                  <span>{reason}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Bottom Form Action Buttons */}
        <div className="flex items-center justify-between pt-4 border-t border-slate-200">
          <button
            type="button"
            onClick={goBack}
            className="px-5 py-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-600 text-xs sm:text-sm font-medium transition-colors cursor-pointer"
          >
            Back
          </button>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={saveAndExit}
              disabled={isSaving}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs sm:text-sm font-medium transition-colors cursor-pointer"
            >
              <Bookmark className="w-4 h-4 text-slate-500" />
              <span>{isSaving ? 'Saving...' : 'Save & exit'}</span>
            </button>

            <button
              type="submit"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm font-medium shadow-md hover:shadow-lg transition-all cursor-pointer"
            >
              <span>Save & continue</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Step3RidesClients;
